import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage } from '@/utils/layer'
import SearchInput from '@/components/search-input/search-input'
import { Center, Column, Expaned, Row } from '@/components/layout/layout'
import { observer } from 'mobx-react'
import { GoodsModel, IGoodsCategory } from '../model'
import { reaction } from 'mobx'
import { getBEMCls } from '@/utils/classname'
import './goods-category.scss'
import classNames from 'classnames'
import { InkWell } from '@/components/inkwell/inkwell'
import { getQiniuImgUrl } from '@/config/qiniu'
import { useLocalStorageState, useMount, useUpdateEffect } from 'ahooks'
import { Empty } from 'antd'
import Button from '@/components/button/button'
import Iconfont from '@/components/iconfont/iconfont'
import useSelectList from '@/utils/hooks/useSelectList'
import classnames from 'classnames'
import useBemClass from '@/utils/hooks/useBEMClass'

type Icategory = Array<{
    name: string
    child: Array<{
        name: string
        items: Array<IGoodsCategory>
    }>
}>

const RecendChoosedListStorageKey = 'RecendChoosedGoodsCategoryList'

interface ChooseGoodsCategoryDialogProps {
    goodsName?: string
    goodsModel: GoodsModel
    selectCategory?: IGoodsCategory[]
    mutiple?: boolean // 是否多选
    onChange?: (value: IGoodsCategory) => void
    onSubmit?: (vals: IGoodsCategory[]) => void
}

const CategoryItem: React.FC<{
    item: IGoodsCategory
    isSelected: boolean
    onClick: (item: IGoodsCategory) => void
}> = props => {
    const gls = useBemClass('goods-category')

    return (
        <InkWell
            data-row
            data-align-items-center
            data-flex-shrink-0
            data-margin-bottom-10
            data-margin-right-10
            onClick={() => {
                props.onClick(props.item)
            }}
            className={classnames(gls('category-content', 'category-item'), {
                [gls('category-content', 'category-item-active')]: props.isSelected,
            })}
        >
            {props.isSelected && (
                <div className={gls('category-content', 'category-item-icon')}>
                    <Iconfont name="iconyixuan" size={14} />
                </div>
            )}

            <img src={getQiniuImgUrl(props.item.img_url)} alt="" />
            <span>{props.item.name}</span>
        </InkWell>
    )
}

const RenderLevels: React.FC<{
    levels: Icategory
    activedLevel1: string
    mutiple?: boolean
    categorySelection: any
    submit: (item: IGoodsCategory) => void
}> = props => {
    const levelRef = useRef<HTMLDivElement | null>(null)

    const renderLevel = useMemo(() => props.levels.find(val => val.name === props.activedLevel1), [
        props.activedLevel1,
        props.levels,
    ])

    useUpdateEffect(() => {
        if (levelRef.current) {
            levelRef.current.scrollTo({
                top: 0,
            })
        }
    }, [props.activedLevel1])

    return (
        <div data-expaned data-column data-overflow-auto ref={levelRef}>
            {renderLevel &&
                renderLevel.child.map((level2, index) => {
                    return (
                        <Column key={index} data-padding-h-16>
                            <span data-font-size-14 data-color-999 data-margin-top-10>
                                {level2.name}
                            </span>
                            <Row data-margin-top-10 style={{ flexWrap: 'wrap' }}>
                                {level2.items.map(item => (
                                    <CategoryItem
                                        key={item.id}
                                        isSelected={!!props.mutiple && props.categorySelection.has(item)}
                                        item={item}
                                        onClick={() => {
                                            props.submit(item)
                                        }}
                                    />
                                ))}
                            </Row>
                        </Column>
                    )
                })}
        </div>
    )
}

export const ChooseGoodsCategoryDialog: React.FC<ChooseGoodsCategoryDialogProps> = observer(props => {
    const [levels, setLevels] = useState<Icategory>([])
    const [activedLevel1, setActivedLevel1] = useState('推荐')
    const [recommands, setRecommands] = useState<IGoodsCategory[]>([])
    const [recentChoosed, setRecendChoosedList] = useState<IGoodsCategory[]>([])
    const [searchStr, setSearchStr] = useState('')
    const [searchList, setSearchList] = useState<IGoodsCategory[]>([])
    const categorySelection = useSelectList<IGoodsCategory>(props.selectCategory || [], 'id')
    const [isFiltered, setFiltered] = useLocalStorageState<boolean>('GOODS_CATEGROY_CHOOSED_FILTER', false)

    const selectNum = useMemo(() => {
        return categorySelection.select.reduce<Record<string, number>>((pre, next) => {
            if (pre[next.group_name]) {
                return {
                    ...pre,
                    [next.group_name]: pre[next.group_name] + 1,
                }
            } else {
                return {
                    ...pre,
                    [next.group_name]: 1,
                }
            }
        }, {})
    }, [categorySelection.select])

    const gls = (...props: string[]) => getBEMCls('goods-category', ...props)
    reaction(
        () => props.goodsModel.goodsCategory,
        () => {
            initLevel()
        },
    )

    useMount(() => {
        props.goodsModel.initGoodsClassInfo()
        initLevel()

        // 取出历史选择的分类
        const listStr = localStorage.getItem(RecendChoosedListStorageKey)
        if (listStr) {
            try {
                if (isFiltered) {
                    setRecendChoosedList(JSON.parse(listStr))
                } else {
                    localStorage.setItem(RecendChoosedListStorageKey, JSON.stringify([]))
                    setFiltered(true)
                }
            } catch {}
        }
    })

    useEffect(() => {
        if (!searchStr.trim().length) return
        const searchList = props.goodsModel.goodsCategory.filter(val => val.name.indexOf(searchStr) !== -1)
        setSearchList(searchList)
    }, [searchStr, props.goodsModel.goodsCategory])

    function initLevel() {
        const list: Icategory = []

        props.goodsModel.goodsCategory.forEach(val => {
            const level1 = list.find(level1 => level1.name === val.group_name)
            if (level1) {
                // 找到了就往里面塞
                const level2 = level1.child.find(level2 => level2.name === val.secondary_group_name)
                if (level2) {
                    level2.items.push(val)
                } else {
                    level1.child.push({
                        name: val.secondary_group_name,
                        items: [val],
                    })
                }
            } else {
                // 找不到就新增一个
                list.push({
                    name: val.group_name,
                    child: [
                        {
                            name: val.secondary_group_name,
                            items: [val],
                        },
                    ],
                })
            }
        })
        setLevels(list)

        // 设置推荐
        if (props.goodsName) {
            const recommand = props.goodsModel.goodsCategory.filter(
                val => val.name.indexOf((props.goodsName as unknown) as string) !== -1,
            )
            setRecommands(recommand)
        }
    }

    const submit = useCallback(
        (item: IGoodsCategory) => {
            if (props.mutiple) {
                categorySelection.toggle(item)
            } else {
                let categoryIndex = recentChoosed.findIndex(i => i.id === item.id)
                if (categoryIndex === -1) {
                    recentChoosed.unshift(item)
                    if (recentChoosed.length > 10) {
                        recentChoosed.pop()
                    }
                    setRecendChoosedList(recentChoosed)
                    localStorage.setItem(RecendChoosedListStorageKey, JSON.stringify(recentChoosed))
                } else {
                    const choosedTmp = recentChoosed.slice()
                    const tmp = choosedTmp[categoryIndex]
                    choosedTmp[categoryIndex] = choosedTmp[0]
                    choosedTmp[0] = tmp

                    setRecendChoosedList(choosedTmp)
                    localStorage.setItem(RecendChoosedListStorageKey, JSON.stringify(choosedTmp))
                }
                props.onChange && props.onChange(item)
                layerManage.pop()
            }
        },
        [categorySelection, props, recentChoosed],
    )

    const RenderRecommend = () => {
        return (
            <Expaned data-column data-overflow-auto>
                {recommands.length > 0 && (
                    <Column data-padding-h-16>
                        <span data-font-size-14 data-color-999 data-margin-top-10>
                            推荐
                        </span>
                        <Row data-margin-top-10 style={{ flexWrap: 'wrap' }}>
                            {recommands.map(item => (
                                <CategoryItem
                                    key={item.id}
                                    isSelected={!!props.mutiple && categorySelection.has(item)}
                                    item={item}
                                    onClick={() => {
                                        submit(item)
                                    }}
                                />
                            ))}
                        </Row>
                    </Column>
                )}
                <Column data-padding-h-16>
                    <span data-font-size-14 data-color-999 data-margin-top-10>
                        最近选择
                    </span>
                    <Row data-margin-top-10 style={{ flexWrap: 'wrap' }}>
                        {recentChoosed.map(item => (
                            <CategoryItem
                                key={item.id}
                                isSelected={!!props.mutiple && categorySelection.has(item)}
                                item={item}
                                onClick={() => {
                                    submit(item)
                                }}
                            />
                        ))}
                    </Row>
                </Column>
            </Expaned>
        )
    }

    return (
        <BaseDialog
            isShow
            styles={{ width: `${props.mutiple ? 930 : 713}px` }}
            title="商品类目"
            onClose={() => layerManage.pop()}
        >
            <Column data-padding-24 className={gls()}>
                <SearchInput
                    placeholder="请输入关键词"
                    onSearch={setSearchStr}
                    onDelete={() => {
                        setSearchStr('')
                    }}
                ></SearchInput>
                <Row>
                    <Row className={gls('category-content')}>
                        {searchStr.trim().length > 0 ? (
                            <Expaned data-column data-overflow-auto>
                                {
                                    <Column data-padding-h-16>
                                        {searchList.length > 0 ? (
                                            <Row data-margin-top-10 style={{ flexWrap: 'wrap' }}>
                                                {searchList.map(item => (
                                                    <CategoryItem
                                                        key={item.id}
                                                        isSelected={!!props.mutiple && categorySelection.has(item)}
                                                        item={item}
                                                        onClick={() => {
                                                            submit(item)
                                                        }}
                                                    />
                                                ))}
                                            </Row>
                                        ) : (
                                            <Center data-margin-top-50>暂无搜索结果</Center>
                                        )}
                                    </Column>
                                }
                            </Expaned>
                        ) : (
                            <Fragment>
                                <Column data-overflow-auto className={gls('level1-item-container')}>
                                    <InkWell
                                        data-row
                                        data-flex-shrink-0
                                        data-align-items-center
                                        onClick={() => setActivedLevel1('推荐')}
                                        className={classNames(gls('category-content', 'level1-item'), {
                                            [gls('category-content', 'active')]: activedLevel1 === '推荐',
                                        })}
                                    >
                                        <span data-padding-left-10>推荐</span>
                                    </InkWell>
                                    {levels.map((val, index) => (
                                        <InkWell
                                            data-row
                                            data-flex-shrink-0
                                            data-align-items-center
                                            data-width-100p
                                            data-position-relative
                                            onClick={() => {
                                                setActivedLevel1(val.name)
                                            }}
                                            className={classNames(gls('category-content', 'level1-item'), {
                                                [gls('category-content', 'active')]: activedLevel1 === val.name,
                                            })}
                                            key={val.name}
                                        >
                                            {!!selectNum[val.name] && (
                                                <div className={gls('badge')}>{selectNum[val.name]}</div>
                                            )}
                                            <span data-padding-left-10>{val.name}</span>
                                        </InkWell>
                                    ))}
                                </Column>
                                {activedLevel1 === '推荐' ? (
                                    <RenderRecommend></RenderRecommend>
                                ) : (
                                    <RenderLevels
                                        levels={levels}
                                        categorySelection={categorySelection}
                                        activedLevel1={activedLevel1}
                                        mutiple={props.mutiple}
                                        submit={item => {
                                            submit(item)
                                        }}
                                    ></RenderLevels>
                                )}
                            </Fragment>
                        )}
                    </Row>
                    {props.mutiple && (
                        <div className={gls('list')}>
                            {categorySelection.select.length ? (
                                <>
                                    <div className={gls('list', 'top')}>
                                        已选择
                                        <div data-margin-h-4 className="them-color">
                                            {categorySelection.select.length}
                                        </div>
                                        个类目
                                    </div>
                                    <Expaned data-overflow-auto data-padding-bottom-10>
                                        {categorySelection.select.map(list => (
                                            <div key={list.id} className={gls('list', 'item')}>
                                                <img
                                                    className={gls('list', 'img')}
                                                    src={getQiniuImgUrl(list.img_url)}
                                                    alt=""
                                                />
                                                <div className="txt">{list.name}</div>
                                                <div data-cursor-pointer>
                                                    <Iconfont
                                                        name="icon6yichu-20"
                                                        size={20}
                                                        onClick={() => {
                                                            categorySelection.remove(list)
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </Expaned>
                                    <div className={gls('list', 'bottom')}>
                                        <Button
                                            types="default"
                                            onClick={() => {
                                                props.onSubmit && props.onSubmit(categorySelection.select)
                                                layerManage.pop()
                                            }}
                                        >
                                            确认
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无选择类目" />
                            )}
                        </div>
                    )}
                </Row>
            </Column>
        </BaseDialog>
    )
})
